import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { orderService, authService } from '../../services/api';
import './AuthPages.css';

import { useDocumentTitle } from '../../hooks/useDocumentTitle';
const STATUT_COULEUR = { 'en_attente':'#c9a84c','confirme':'#5b8dee','expedie':'#a78bfa','livre':'#4caf6e','annule':'#e05252' };
const STATUT_LABEL   = { 'en_attente':'En attente','confirme':'Confirmé','expedie':'Expédié','livre':'Livré','annule':'Annulé' };

export default function DashboardPage() {
  useDocumentTitle('Mon espace client');

  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);

  // Changement de mot de passe
  const [showPwdForm, setShowPwdForm] = useState(false);
  const [pwd, setPwd]         = useState({ current: '', next: '', confirm: '' });
  const [pwdMsg, setPwdMsg]   = useState(null);   // { type: 'ok'|'err', text }
  const [pwdLoading, setPwdLoading] = useState(false);

  async function handlePwdSubmit(e) {
    e.preventDefault();
    setPwdMsg(null);
    if (pwd.next.length < 8) {
      setPwdMsg({ type: 'err', text: 'Le nouveau mot de passe doit contenir au moins 8 caractères.' });
      return;
    }
    if (pwd.next !== pwd.confirm) {
      setPwdMsg({ type: 'err', text: 'La confirmation ne correspond pas.' });
      return;
    }
    setPwdLoading(true);
    try {
      await authService.update({ current_password: pwd.current, new_password: pwd.next });
      setPwdMsg({ type: 'ok', text: 'Mot de passe modifié avec succès.' });
      setPwd({ current: '', next: '', confirm: '' });
      setTimeout(() => { setShowPwdForm(false); setPwdMsg(null); }, 2000);
    } catch (err) {
      setPwdMsg({ type: 'err', text: err.message });
    } finally {
      setPwdLoading(false);
    }
  }

  useEffect(() => {
    orderService.myOrders().then(setOrders).catch(() => {}).finally(() => setLoading(false));
  }, []);

  function handleLogout() { logout(); navigate('/', { replace: true }); }

  return (
    <div className="dashboard">
      <div className="container" style={{ padding: '64px 24px' }}>
        <div className="dashboard__header">
          <div>
            <p className="dashboard__overtitle">Espace client</p>
            <h1 className="dashboard__titre">Bonjour, <span className="or">{user?.prenom || 'Cliente'}</span> 👋</h1>
            <p className="dashboard__sous">{user?.email}</p>
          </div>
          <button className="btn-outline dashboard__logout" onClick={handleLogout}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Se déconnecter
          </button>
        </div>
        <hr style={{ border:'none', borderTop:'1px solid rgba(201,168,76,0.15)', margin:'32px 0 48px' }} />
        <div className="dashboard__grid">
          <section className="dashboard__card">
            <h2 className="dashboard__card-titre">Mes informations</h2>
            <div className="dashboard__info-grid">
              {[['Prénom', user?.prenom],['Nom', user?.nom],['Email', user?.email],['Rôle', user?.role]].map(([l,v]) => (
                <div key={l} className="dashboard__info-item">
                  <span className="dashboard__info-label">{l}</span>
                  <span className="dashboard__info-val">{v || '—'}</span>
                </div>
              ))}
            </div>
          </section>
          <section className="dashboard__card dashboard__card--wide">
            <h2 className="dashboard__card-titre">Mes commandes</h2>
            {loading ? <div className="spinner" /> : orders.length === 0 ? (
              <div style={{ textAlign:'center', padding:'32px 0', color:'var(--gris)' }}>
                <p style={{ marginBottom:16 }}>Vous n'avez pas encore passé de commande.</p>
                <a href="/catalogue" className="btn-or" style={{ display:'inline-flex' }}>Découvrir la collection</a>
              </div>
            ) : (
              <div className="dashboard__orders">
                {orders.map(cmd => (
                  <div key={cmd.id} className="dashboard__order-row">
                    <div>
                      <p className="dashboard__order-id">#{cmd.id}</p>
                      <p className="dashboard__order-produit">{cmd.items?.map(i => i.nom).join(', ') || '—'}</p>
                    </div>
                    <p className="dashboard__order-date">{new Date(cmd.date_commande).toLocaleDateString('fr-FR')}</p>
                    <span className="dashboard__order-statut" style={{ color: STATUT_COULEUR[cmd.statut] || 'var(--gris)' }}>
                      ● {STATUT_LABEL[cmd.statut] || cmd.statut}
                    </span>
                    <p className="dashboard__order-total">{Number(cmd.total).toFixed(2)} €</p>
                  </div>
                ))}
              </div>
            )}
          </section>
          <section className="dashboard__card">
            <h2 className="dashboard__card-titre">Sécurité</h2>
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
              <button className="btn-outline" style={{ fontSize:'0.82rem', padding:'10px 20px' }}
                onClick={() => { setShowPwdForm(v => !v); setPwdMsg(null); }}>
                {showPwdForm ? 'Annuler' : 'Changer de mot de passe'}
              </button>
              {showPwdForm && (
                <form onSubmit={handlePwdSubmit} style={{ display:'flex', flexDirection:'column', gap:10 }}>
                  <input type="password" placeholder="Mot de passe actuel" required autoComplete="current-password"
                    value={pwd.current} onChange={e => setPwd(p => ({ ...p, current: e.target.value }))}
                    style={{ padding:'10px 12px', background:'#1a1a1a', border:'1px solid rgba(255,255,255,0.12)', borderRadius:4, color:'var(--blanc)' }} />
                  <input type="password" placeholder="Nouveau mot de passe (8 car. min)" required autoComplete="new-password"
                    value={pwd.next} onChange={e => setPwd(p => ({ ...p, next: e.target.value }))}
                    style={{ padding:'10px 12px', background:'#1a1a1a', border:'1px solid rgba(255,255,255,0.12)', borderRadius:4, color:'var(--blanc)' }} />
                  <input type="password" placeholder="Confirmer le nouveau mot de passe" required autoComplete="new-password"
                    value={pwd.confirm} onChange={e => setPwd(p => ({ ...p, confirm: e.target.value }))}
                    style={{ padding:'10px 12px', background:'#1a1a1a', border:'1px solid rgba(255,255,255,0.12)', borderRadius:4, color:'var(--blanc)' }} />
                  {pwdMsg && (
                    <p style={{ fontSize:'0.82rem', color: pwdMsg.type === 'ok' ? '#4caf6e' : '#e05252' }}>{pwdMsg.text}</p>
                  )}
                  <button type="submit" className="btn-or" disabled={pwdLoading}
                    style={{ fontSize:'0.82rem', padding:'10px 20px', justifyContent:'center' }}>
                    {pwdLoading ? 'Enregistrement…' : 'Valider'}
                  </button>
                </form>
              )}
              <button onClick={handleLogout} style={{ background:'none', border:'1px solid #e05252', color:'#e05252', borderRadius:4, padding:'10px 20px', cursor:'pointer', fontSize:'0.82rem', fontFamily:'var(--font-corps)', textTransform:'uppercase', letterSpacing:'0.08em' }}>
                Se déconnecter
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}